import { useState, useEffect, useRef } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';

const MIN_REASON = 10;
const MAX_REASON = 500;

interface RejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading?: boolean;
  employeeName?: string;
}

export function RejectModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  employeeName,
}: RejectModalProps) {
  const [reason, setReason] = useState('');
  const rejectButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setReason('');
    }
  }, [isOpen]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON;
  const tooLong = trimmed.length > MAX_REASON;
  const valid = !tooShort && !tooLong;

  const handleConfirm = () => {
    if (!valid) return;
    onConfirm(trimmed);
    setReason('');
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const counterClass = tooLong
    ? 'text-red-600'
    : tooShort
      ? 'text-gray-500'
      : 'text-green-600';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Odmítnout výkaz"
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Zrušit
          </Button>
          <Button
            ref={rejectButtonRef}
            variant="danger"
            onClick={handleConfirm}
            isLoading={isLoading}
            disabled={!valid || isLoading}
          >
            Odmítnout
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-gray-600">
          {employeeName ? (
            <>
              Opravdu chceš odmítnout výkaz uživatele{' '}
              <span className="font-medium">{employeeName}</span>?
            </>
          ) : (
            'Opravdu chceš výkaz odmítnout?'
          )}
        </p>
        <p className="text-sm text-gray-500">
          Uživatel dostane e-mail s důvodem a bude muset výkaz opravit a znovu
          odeslat.
        </p>
        <div>
          <label
            htmlFor="reason"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Důvod odmítnutí <span className="text-red-500">*</span>
          </label>
          <textarea
            id="reason"
            rows={4}
            maxLength={MAX_REASON + 50}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Popiš co je špatně a co má uživatel opravit (min 10 znaků)…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isLoading}
          />
          <div className="flex justify-between mt-1 text-xs">
            <span className={tooShort ? 'text-gray-500' : 'text-transparent'}>
              {tooShort
                ? `Ještě ${MIN_REASON - trimmed.length} znaků (min ${MIN_REASON})`
                : 'placeholder'}
            </span>
            <span className={counterClass}>
              {trimmed.length} / {MAX_REASON}
              {tooLong ? ` — překročeno o ${trimmed.length - MAX_REASON}` : ''}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}
